import { ChevronDown, Sparkles } from 'lucide-react';
import type { AiCampaignForm } from '@/features/campaigns/types/campaign';
import { AI_AUDIENCE_CATEGORIES } from '@/features/campaigns/constants/campaignOptions';
import { CommonSelect } from '@/features/campaigns/components/CommonSelect';
import { AudienceDemographic } from '@/features/campaigns/components/AudienceDemographic';
import { FieldError, FieldLabel, campInputClass } from '@/features/campaigns/components/FormPrimitives';

type AudienceVisionSectionProps = {
  formData: AiCampaignForm;
  setFormData: React.Dispatch<React.SetStateAction<AiCampaignForm>>;
  errors: Record<string, string>;
  clearFieldError: (field: string) => void;
  advanced: boolean;
  setAdvanced: React.Dispatch<React.SetStateAction<boolean>>;
};

export function AudienceVisionSection({
  formData,
  setFormData,
  errors,
  clearFieldError,
  advanced,
  setAdvanced,
}: AudienceVisionSectionProps) {
  return (
    <section className="camp-section text-left">
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[var(--dash-brand)]" />
        <h2 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">
          Audience & vision
        </h2>
      </div>

      <CommonSelect
        label="Ad category"
        name="audienceCategory"
        value={formData.audienceCategory}
        options={AI_AUDIENCE_CATEGORIES.map((label) => ({ key: label, label }))}
        error={errors.audienceCategory}
        required
        hint="Helps AI match the right venues"
        onChange={(_n, value) => {
          clearFieldError('audienceCategory');
          setFormData((prev) => ({ ...prev, audienceCategory: value }));
        }}
      />

      <div className="mt-4 flex flex-col gap-1.5">
        <FieldLabel
          required
          hint={`${formData.vision.length}/600 · be specific about who and why`}
        >
          Campaign vision
        </FieldLabel>
        <textarea
          value={formData.vision}
          maxLength={600}
          rows={4}
          onChange={(e) => {
            clearFieldError('vision');
            setFormData((prev) => ({ ...prev, vision: e.target.value }));
          }}
          placeholder="Example: Promote our weekend sale to young professionals near gyms and malls in Bhopal."
          className={`${campInputClass} resize-y ${errors.vision ? 'is-invalid' : ''}`}
        />
        <FieldError message={errors.vision} />
      </div>

      <button
        type="button"
        onClick={() => setAdvanced((v) => !v)}
        className="mt-4 flex w-full items-center justify-between rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] px-4 py-3 text-left transition hover:bg-white"
        aria-expanded={advanced}
      >
        <span>
          <span className="block text-sm font-semibold text-[var(--dash-ink)]">
            Advanced audience (optional)
          </span>
          <span className="block text-xs text-[var(--dash-muted)]">
            Economic activity & lifestyle filters
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[var(--dash-muted)] transition ${
            advanced ? 'rotate-180' : ''
          }`}
        />
      </button>
      {advanced ? (
        <div className="mt-3">
          <AudienceDemographic
            formData={formData}
            onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
          />
        </div>
      ) : null}
    </section>
  );
}
